import { Injectable } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import { DimensionItem, DimesionChild, ReverseDimensionItem, ReverseDimensionMap } from '../interfaces';
import { DataService } from '../services';
import { environment } from '../../../environments/environment';

/**
 * DimensionHandler class encapsulate the specific implementation issues
 * public methode: reveseDimensionStructure
 *  It reverse the child - parent relation to parent - child relation,
 *  which needed by the PIVOT table logic for hiarachic dimensions.
 */
class DimensionHandler {
  set mysqlDriver(mysql: boolean) {
    this.isMysqlDriver = mysql;
  }
  static readonly MAXLEVEL = 5;
  private isMysqlDriver!: boolean;
  private source_dimensions: DimensionItem[] = [];
  private result_dimensions: ReverseDimensionMap = new Map<string, ReverseDimensionItem>();
  // private dimensions: ReverseDimensionItem[] = [];
  private reverse_object: any = null;
  private label!: string;

  // construct(mysqlDriver: boolean) {    
  constructor() {
    // this.dimensions = [];
    // this.isMysqlDriver = mysqlDriver
  }

  reveseDimensionStructure(dimension_items: DimensionItem[], lang: string): ReverseDimensionMap {
    this.source_dimensions = dimension_items;
    this.label = `label_${lang}`;
    this.result_dimensions = new Map<string, ReverseDimensionItem>();

    this.source_dimensions.forEach((item) => {
      this.createDimensionItem(item);
    });
    // TODO THe next two lines for debuging purpose
    // console.debug('[DimensionService] Dimensions array: ', JSON.stringify(this.dimensions, undefined, 2))
    // console.debug('[DimensionService] Result dimensions: ', this.result_dimensions)
    return this.result_dimensions;
  }

  private removeReportId(id: string): string {
    if (this.isMysqlDriver) {
      const [report_id, ...rest] = id.split('_');
      // the rest may have "_" in it
      const dimensionCode = rest.join('_');
      return dimensionCode;
    } else {
      return id;
    }
  }

  private createDimensionItem(item: DimensionItem): void {
    type ItemKey = keyof typeof item.label_hu | keyof typeof item.label_en;

    const id = this.removeReportId(item.id);

    const value: ReverseDimensionItem = { id: id, ord: item.ord, grp: item.grp };

    if (item.parent) {
      // create with child(s)
      this.reverse_object = null;
      this.reveseDimensionItem(item.parent, item.id, item[this.label as ItemKey]);
      value.code = this.removeReportId(this.reverse_object.id);      
      value.label = this.reverse_object.label;
      value['child'] = this.reverse_object.child;
    } else {
      // create with no child
      value.label = item[this.label as ItemKey];
      value.code = id;
    }
    // TODO THe next line for debuging purpose
    // this.dimensions.push(value);
    this.result_dimensions.set(id, value);
  }

  private reveseDimensionItem(item: any, id: string | null, label: string): any {
    if (item) this.reveseDimensionItem(item.parent, item.id, item[this.label]);

    const childDimension = { id, label };
    this.buildReverseRelation(childDimension);
    return childDimension;
  }

  private buildReverseRelation(item: any): ReverseDimensionItem {
    if (!this.reverse_object) {
      // level #1
      this.reverse_object = item;
    } else {
      const id = this.removeReportId(item.id);

      const child: DimesionChild = { code: id, label: item.label };
      if (!this.reverse_object.hasOwnProperty('child')) {
        // level #2
        this.reverse_object['child'] = child;
      } else if (!this.reverse_object.child.hasOwnProperty('child')) {
        // level #3
        this.reverse_object.child['child'] = child;
      } else if (!this.reverse_object.child.child.hasOwnProperty('child')) {
        // level #4
        this.reverse_object.child.child['child'] = child;
      } else if (!this.reverse_object.child.child.child.hasOwnProperty('child')) {
        // level #5
        this.reverse_object.child.child.child['child'] = child;
      } else {
        throw new Error(`Too deep hierarchy level. Use maximum ${DimensionHandler.MAXLEVEL} level.`);
      }
    }
    return this.reverse_object;
  }
}

@Injectable({
  providedIn: 'root',
})
export class DimensionService {
  private reverse_dimensions: ReverseDimensionMap = new Map<string, ReverseDimensionItem>();
  private odataCtx: any;
  private label = 'label_hu';
  private dimensionHandler;
  private isMysqlDriver!: boolean;

  constructor(private dataService: DataService) {
    this.odataCtx = this.dataService.odataCtx;
    this.isMysqlDriver = environment.DB_DRIVER === 'mysql' || false;
    this.dimensionHandler = new DimensionHandler();
    this.dimensionHandler.mysqlDriver = this.isMysqlDriver;
  }

  /**
   * Get expanded dimendion data for a report
   * params:
   *  lang: the current locale of the application
   *  reportId: the unic id of the report to be served
   * return: a Map of flattened dimension items
   */
  async getDimensions(lang: string, reportId: string | null): Promise<ReverseDimensionMap> {
    this.label = `label_${lang}`;

    const dataSource = new DataSource({
      store: this.odataCtx.Dimension,
      select: ['id', this.label, 'grp', 'ord'],
      filter: [['report_id', reportId]],
      sort: ['grp', 'ord'],
      expand: [
        // `parent($select=id,${this.label};$orderby=order;$expand=parent($select=id,${this.label};$orderby=order;$expand=parent($select=id,${this.label};$orderby=order;$expand=parent($select=id,${this.label};$orderby=order))))`,
        `parent($select=id,${this.label};$expand=parent($select=id,${this.label};$expand=parent($select=id,${this.label};$expand=parent($select=id,${this.label}))))`,
      ],
      paginate: false,
    });

    await dataSource.load().then((data: DimensionItem[]) => {
      try {
        // const dimensionHandler = new DimensionHandler();
        this.reverse_dimensions = this.dimensionHandler.reveseDimensionStructure(data, lang);
      } catch (e) {
        console.log("Error, when reversing dimensions: ", e);
      }
    });

    return this.reverse_dimensions;
  }

  /**
   * Get Parent dimendion labels for a report
   * params:
   *  lang: the current locale of the application
   *  reportId: the unic id of the report to be served
   * return: a Map of flattened dimension items
   */
  async getParentDimensionLabels(lang: string, reportId: string): Promise<any[]> {
    const parentDimensionCodes: string[] = await this.getParentDimensionCodes(reportId);
    return this.getDimensionLabelsRecords(lang, reportId, parentDimensionCodes);
  }

  /**
   * Get Leaf dimendion labels for a report
   * params:
   *  lang: the current locale of the application
   *  reportId: the unic id of the report to be served
   * return: a Map of flattened dimension items
   */
  async getLeafDimensionLabels(lang: string, reportId: string): Promise<any[]> {
    const parentDimensionCodes: string[] = await this.getParentDimensionCodes(reportId);
    return this.getDimensionLabelsRecords(lang, reportId, parentDimensionCodes, true);
  }

  async getChildlessDimensionLabels(lang: string, reportId: string | null): Promise<string[]> {
    const dataSource = new DataSource({
      store: this.odataCtx.Dimension,
      select: ['id', 'report_id', 'grp', 'ord', 'children'],
      filter: ['report_id', reportId],
      sort: ['report_id', 'id', 'grp', 'ord'],
      expand: 'children($select=id;$expand=children($select=id;$expand=children($select=id;$expand=children($select=id;$expand=children))))',
      // map: (item) => { return this.splitDimensionId(item.parent_id) },
      map: (item) => {
        return { item }
      },
      paginate: false,
    });

    const result = dataSource.load().then(
      (data: any[]) => {
        return data.filter((dimension) => {
          dimension.children.length > 0
        });
      }
      // (data: string[]) => { return Array.from(new Set(data)) },
    );

    return result;
  }

  /**
   * Get Parent dimendion codes for a report
   * params:
   *  reportId: the unic id of the report to be served
   * return: a Map of flattened dimension items
   */
  async getParentDimensionCodes(reportId: string | null): Promise<string[]> {

    const dataSource = new DataSource({
      store: this.odataCtx.Dimension,
      select: ['parent_id'],
      filter: [['report_id', reportId], ['parent_id', '<>', null]],
      sort: 'parent_id',
      map: (item) => {
        return this.splitDimensionId(item.parent_id)
      },
      paginate: false,
    });

    const data = await dataSource.load()
    const result: string[] = Array.from(new Set(data));
    
    return result;
  }

  async getDimensionLabelsRecords(lang: string, reportId: string, dimensionCodes: string[], excludeParents: boolean = false): Promise<any[]> {
    this.label = `label_${lang}`;

    const dataSource = new DataSource({
      store: this.odataCtx.Dimension,
      select: ['grp', 'ord', 'id', this.label],
      filter: ['report_id', reportId],
      sort: ['grp', 'ord'],
      map: (element) => {
        return { grp: element.grp, ord: element.ord, code: this.splitDimensionId(element.id), label: element[this.label] }
      },
      paginate: false,
    });

    const result: Promise<any[]> = dataSource.load().then((data: any[]) => {
      return data.filter((dimension: any[]) => {
        // @ts-ignore
        return (excludeParents) ? !dimensionCodes.includes(dimension.code) : dimensionCodes.includes(dimension.code);
      })
    });

    return result;
  }

  private splitDimensionId(id: string): string {
    if (!this.isMysqlDriver) return id;
    const [report_id, ...rest] = id.split('_');
    // the rest may have "_" in it
    const dimensionCode = rest.join('_');
    return dimensionCode;
  }
}
